import {useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Empty, Input, Spin, Switch, Tag} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {useExchanges} from 'components/exchangeSelect/ExchangeSelect';
import {actionError, loadError, unionError} from '../shared/errors';
import {ActionButton, HowItWorks, Tip} from '../shared/ui';
import {
  ASSIGN_ARTICLE_EXCHANGE,
  GET_NEWS_SOURCES,
  GET_UNROUTED_ARTICLES,
} from './resolvers';
import {HOW_UNPLACED, UNPLACED} from './copy';
import styles from '../shared/news.module.scss';

const STOP_WORDS = (
  'the a an and or of in on at to for with from by as is are was were be ' +
  'its it this that after over new says said amid le la les des de du un ' +
  'une et en au aux pour par sur dans avec est sont'
).split(' ');

// A few distinctive words from the headline to offer as "next time" words.
export const suggestWords = title =>
  (title || '')
    .split(/[^\p{L}\p{N}'-]+/u)
    .filter(word => word.length > 3 && !STOP_WORDS.includes(word.toLowerCase()))
    .filter((word, i, words) => words.indexOf(word) === i)
    .slice(0, 4);

const UnplacedArticle = ({article, exchanges, canUpdate, onDone}) => {
  const [learn, setLearn] = useState(false);
  const [word, setWord] = useState('');
  const [busy, setBusy] = useState(null);

  const [assign] = useMutation(ASSIGN_ARTICLE_EXCHANGE, {
    onError: actionError('place this article'),
  });

  const place = async exchange => {
    setBusy(exchange.id);
    const learnKeyword = learn ? word.trim() || undefined : undefined;
    const {data} = await assign({
      variables: {articleId: article.id, exchangeId: exchange.id, learnKeyword},
    });
    setBusy(null);
    const result = data?.assignArticleExchange;
    if (!result) return;
    if (result.__typename === 'Error') {
      return unionError('place this article', result);
    }
    toast.success(UNPLACED.placed(exchange.code, learnKeyword));
    onDone();
  };

  return (
    <li className={styles.card}>
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>{article.title}</span>
        <span className={styles.muted}>
          {article.sourceName || article.sourceHost}
          {article.publishedDate
            ? ` · ${moment(article.publishedDate).fromNow()}`
            : ''}
        </span>
      </div>
      {article.excerpt ? (
        <p className={styles.excerpt}>{article.excerpt}</p>
      ) : null}
      <Tip
        label={
          <a href={article.canonicalUrl} target="_blank" rel="noreferrer">
            {UNPLACED.open}
          </a>
        }
        tip={UNPLACED.openTip}
      />
      <div className={styles.cardActions}>
        {exchanges.map(exchange => (
          <ActionButton
            key={exchange.id}
            allowed={canUpdate}
            tip={UNPLACED.isNewsTip(exchange.code)}
            loading={busy === exchange.id}
            onClick={() => place(exchange)}>
            {UNPLACED.isNews(exchange.code)}
          </ActionButton>
        ))}
        {/* "Not stock market news" returns once news-service adds
            rejectIngestedArticle (plan §12). */}
      </div>
      <div>
        <Switch size="small" checked={learn} onChange={setLearn} />{' '}
        <Tip label={UNPLACED.learnGeneric} tip={UNPLACED.learnTip} />
        {learn ? (
          <div className={styles.block}>
            <Input
              value={word}
              placeholder={UNPLACED.learnPlaceholder}
              onChange={e => setWord(e.target.value)}
            />
            <div className={styles.keywordChips}>
              <span className={styles.muted}>{UNPLACED.suggestions}</span>
              {suggestWords(article.title).map(suggestion => (
                <Tag
                  key={suggestion}
                  className={styles.pill}
                  onClick={() => setWord(suggestion)}>
                  {suggestion}
                </Tag>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </li>
  );
};

// Articles no rule could place on one exchange. The query takes no
// exchange, so this lists every exchange's.
const UnplacedArticlesModal = ({canUpdate, onChanged}) => {
  const {exchanges} = useExchanges();
  const articlesQuery = useQuery(GET_UNROUTED_ARTICLES, {
    variables: {limit: 100},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });
  const sourcesQuery = useQuery(GET_NEWS_SOURCES, {
    variables: {limit: 100},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const response = articlesQuery.data?.unroutedArticles;
  const articles =
    response?.__typename === 'IngestedArticleList' ? response.data : [];
  const failed = response?.__typename === 'Error' || articlesQuery.error;
  const sources =
    sourcesQuery.data?.newsSources?.__typename === 'RegisteredNewsSourceList'
      ? sourcesQuery.data.newsSources.data
      : [];

  // The exchanges the article's website feeds; all of them if it isn't known.
  const exchangesFor = article => {
    const ids = (
      sources.find(source => source.id === article.sourceId)?.coverage || []
    ).map(row => row.exchangeId);
    const fed = exchanges.filter(exchange => ids.includes(exchange.id));
    return fed.length ? fed : exchanges;
  };

  const done = () => {
    articlesQuery.refetch();
    onChanged();
  };

  return (
    <div className={styles.modalRoot}>
      <h2>{UNPLACED.title(articles.length)}</h2>
      <HowItWorks id="unplaced">{HOW_UNPLACED}</HowItWorks>
      {failed ? (
        <Alert
          type="error"
          showIcon
          message={loadError(
            'these articles',
            response?.__typename === 'Error' ? response : articlesQuery.error,
          )}
        />
      ) : null}
      {articlesQuery.loading ? <Spin className={styles.spinner} /> : null}
      {!articlesQuery.loading && !failed && !articles.length ? (
        <Empty description={UNPLACED.empty} />
      ) : null}
      <ul className={styles.cardList}>
        {articles.map(article => (
          <UnplacedArticle
            key={article.id}
            article={article}
            exchanges={exchangesFor(article)}
            canUpdate={canUpdate}
            onDone={done}
          />
        ))}
      </ul>
    </div>
  );
};

export default UnplacedArticlesModal;
